import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import {
  ArrowLeft, Loader2, AlertTriangle, Sparkles, BadgeCheck,
  ThumbsUp, Target, TrendingUp, Calendar,
} from "lucide-react";
import { useTruthState } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PassportPackView — read-only Verified Progress Pack viewer.
 * Route: /pack/view/:token. Backend audits every view.
 * Open to authenticated learner (own pack) or any instructor/admin.
 */
export default function PassportPackView() {
  const { token } = useParams();
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const truth = useTruthState();

  const load = useCallback(async () => {
    try {
      await truth.run(
        async () => {
          const { data } = await axios.get(
            `${API}/api/passport/pack/view/${token}`,
            { withCredentials: true },
          );
          setPack(data);
          setError("");
        },
        { failedMessage: "Unable to load pack." },
      );
    } catch (err) {
      // Keep the local `error` string for the legacy render fork
      // that renders the pack-error panel. TruthReceipt now owns
      // the transient failure with Retry + Dismiss.
      setError(err?.response?.data?.detail || "Unable to load pack");
    }
    setLoading(false);
  }, [token, truth]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !pack) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] p-6 flex flex-col items-center justify-center text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-3" />
        <p className="text-base font-bold text-slate-900 dark:text-white">Pack unavailable</p>
        <p className="text-sm text-slate-500 mt-1 max-w-xs">{error || "This Verified Progress Pack has expired or was revoked."}</p>
        <div className="w-full max-w-xs mt-4">
          <TruthReceipt state={truth.state} message={truth.message} onRetry={load} onDismiss={() => truth.setState(null)} />
        </div>
        <button
          data-testid="pack-view-back"
          onClick={() => goBack()}
          className="mt-4 text-sm font-bold text-blue-600 hover:underline"
        >
          Go back
        </button>
      </div>
    );
  }

  const snap = pack.passport_snapshot || {};
  const engagement = snap.engagement || {};
  const theory = engagement.theory_progress || {};
  const confidence = engagement.confidence || {};
  const summary = pack.summary || {};
  const reviews = pack.ai_reviews || [];
  const expiresAt = pack.expires_at ? new Date(pack.expires_at).toLocaleDateString() : "–";

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24" data-testid="pack-view-page">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button
            data-testid="pack-view-back-btn"
            onClick={() => goBack()}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <BadgeCheck className="w-5 h-5 text-emerald-600" />
          <p className="font-extrabold text-slate-900 dark:text-white text-sm flex-1">Verified Progress Pack</p>
          <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/30 px-2 py-1 rounded-full">
            Guidance only
          </span>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-4 space-y-4">
        {/* Expiry banner */}
        <div
          data-testid="pack-expires"
          className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 flex items-center gap-2 text-xs text-slate-500"
        >
          <Calendar className="w-3.5 h-3.5" />
          <span>Pack link expires <strong className="text-slate-700 dark:text-slate-300">{expiresAt}</strong></span>
        </div>

        {/* Snapshot */}
        <section
          data-testid="pack-snapshot"
          className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5"
        >
          <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-3">Passport snapshot</p>
          <div className="space-y-3">
            <Row label="Level guidance" value={snap.level_estimate?.label || summary.level_guidance || "–"} />
            <Row
              label="Theory progress"
              value={`${theory.percent || 0}%`}
              sub={`${theory.completed || 0}/${theory.total || 0} lessons`}
            />
            <Row
              label="Confidence avg"
              value={confidence.average_score != null ? `${confidence.average_score}%` : "–"}
              sub={`${confidence.checks_completed || 0} check-ins`}
            />
            <Row
              label="Completed lessons"
              value={(snap.lessons?.total_completed) ?? "–"}
            />
          </div>
        </section>

        {/* Summary */}
        <section
          data-testid="pack-summary"
          className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5 space-y-4"
        >
          <p className="text-xs uppercase tracking-wide text-slate-500 font-bold">Aggregate AI summary</p>
          <ListBlock
            icon={<ThumbsUp className="w-4 h-4 text-emerald-600" />}
            label="Strengths"
            items={summary.strengths}
            testId="pack-strengths"
            emptyLabel="Limited evidence yet"
          />
          <ListBlock
            icon={<Target className="w-4 h-4 text-amber-600" />}
            label="Weaknesses"
            items={summary.weaknesses}
            testId="pack-weaknesses"
            emptyLabel="No weaknesses flagged"
          />
          <ListBlock
            icon={<TrendingUp className="w-4 h-4 text-blue-600" />}
            label="Next steps"
            items={summary.next_steps}
            testId="pack-next-steps"
            emptyLabel="Continue structured lessons"
          />
        </section>

        {/* AI reviews */}
        <section
          data-testid="pack-reviews"
          className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-indigo-600" />
            <p className="text-xs uppercase tracking-wide text-slate-500 font-bold">Recent clip reviews</p>
          </div>
          {reviews.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">No AI reviews attached.</p>
          ) : (
            <div className="space-y-3">
              {reviews.slice(0, 5).map((r, i) => (
                <div
                  key={r.id || i}
                  data-testid={`pack-review-${i}`}
                  className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-600 dark:text-slate-300"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-slate-900 dark:text-white">
                      {r.level_guidance || "Developing"}
                    </span>
                    {r.created_at && (
                      <span className="text-[10px] text-slate-400">
                        {new Date(r.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {r.summary && <p className="leading-relaxed">{r.summary}</p>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Disclaimer */}
        <div
          data-testid="pack-disclaimer"
          className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 p-4 flex gap-3"
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-[12px] text-amber-900 dark:text-amber-200 leading-relaxed">
            {pack.disclaimer || "This pack is guidance only — not a pass prediction or readiness guarantee. All readiness calls remain with the instructor."}
          </p>
        </div>
      </main>
    </div>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-bold text-slate-900 dark:text-white">{label}</p>
        {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
      </div>
      <p className="text-sm font-extrabold text-slate-900 dark:text-white">{value}</p>
    </div>
  );
}

function ListBlock({ icon, label, items, testId, emptyLabel }) {
  const rows = Array.isArray(items) ? items : [];
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-1.5">
        {icon}
        <p className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">{label}</p>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-400 italic">{emptyLabel}</p>
      ) : (
        <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-300">
          {rows.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
